import { ImportEntityNode } from "services/KnowfullyAdminClient";

export type ImportEntityNodeModel = ImportEntityNode & {
  subRows?: ImportEntityNodeModel[];
};

/**
 * Rebuilds the containment tree the graph endpoint pre-flattened. Products and orphan skus are roots;
 * everything else hangs off its parentId. Shared entities keep a canonical row in the shared band and
 * appear here only as reference rows under each sku that uses them.
 */
export function mapToEntityTree(
  nodes?: ImportEntityNode[]
): ImportEntityNodeModel[] {
  const all = nodes ?? [];
  const childrenByParent = new Map<string, ImportEntityNodeModel[]>();

  all.forEach((node) => {
    if (!node.parentId) {
      return;
    }

    const siblings = childrenByParent.get(node.parentId) ?? [];
    siblings.push(node as ImportEntityNodeModel);
    childrenByParent.set(node.parentId, siblings);
  });

  const attach = (
    node: ImportEntityNodeModel,
    seen: Set<string>
  ): ImportEntityNodeModel => {
    const children = (childrenByParent.get(node.detailId!) ?? []).filter(
      (child) => !seen.has(child.detailId!)
    );

    if (children.length === 0) {
      return { ...node, subRows: undefined } as ImportEntityNodeModel;
    }

    const descended = new Set(seen).add(node.detailId!);

    return {
      ...node,
      subRows: children.map((child) => attach(child, descended)),
    } as ImportEntityNodeModel;
  };

  return all
    .filter((node) => node.isRoot)
    .map((node) => attach(node as ImportEntityNodeModel, new Set<string>()));
}

/** Canonical rows for entities created once and referenced by several skus. */
export function sharedEntities(
  nodes?: ImportEntityNode[]
): ImportEntityNodeModel[] {
  return (nodes ?? []).filter(
    (node) => node.isShared && !node.isReference
  ) as ImportEntityNodeModel[];
}

export const entityTreeTableProps = {
  enableFilters: false,
  enableDensityToggle: false,
  enableColumnActions: false,
  enableFullScreenToggle: false,
  enableHiding: false,
  enableExpandAll: true,
  enableExpanding: true,
  enablePagination: false,
  enableSorting: false,
};
